import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../apiClient'; // Import the new apiClient
import { useAuth } from '../contexts/AuthContext'; // Import useAuth for role checking
import ConfirmationModal from '../components/DeleteConfirmationModal';
import './EmployeeManagement.css';

// Reusable Modal Component (as defined previously)
const Modal = ({ show, onClose, title, children }) => {
  if (!show) {
    return null;
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button onClick={onClose} className="btn-icon" style={{ fontSize: '1.5em' }}>&times;</button>
        </div>
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );
};

function EmployeeManagement() {
  const { user } = useAuth(); // Get current user for role checking
  const [employees, setEmployees] = useState([]);
  const [changeManagers, setChangeManagers] = useState([]); // For Admin dropdown
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState(null); // For editing
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    department: '',
    job_position: '',
    role: '',
    assigned_manager_id: '' // For Admin to assign Change Manager
  });
  const [apiError, setApiError] = useState(null); // For modal errors
  const [successMessage, setSuccessMessage] = useState(''); // For success messages
  const [uploadStatus, setUploadStatus] = useState(''); // For bulk upload feedback
  const [filterManagerId, setFilterManagerId] = useState(''); // For Admin filtering by Change Manager
  const [selectedEmployees, setSelectedEmployees] = useState(new Set()); // For bulk operations
  const [bulkDeleting, setBulkDeleting] = useState(false); // For bulk delete loading state
  const fileInputRef = useRef(null); // Ref for file input

  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: '',
    cancelText: 'Cancel',
    type: 'danger',
    onConfirm: null,
    isLoading: false
  });

  // Helper functions for confirmation modal
  const openConfirmationModal = ({ title, message, confirmText, type = 'danger', onConfirm }) => {
    setConfirmationModal({
      isOpen: true,
      title,
      message,
      confirmText,
      cancelText: 'Cancel',
      type,
      onConfirm,
      isLoading: false
    });
  };

  const closeConfirmationModal = () => {
    setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
  };

  const setConfirmationLoading = (isLoading) => {
    setConfirmationModal(prev => ({ ...prev, isLoading }));
  };

  // Get available role options based on current user's role
  const getAvailableRoles = () => {
    if (user?.role === 'Admin') {
      return [
        { value: 'Admin', label: 'Admin' },
        { value: 'Change Manager', label: 'Change Manager' },
        { value: 'Team Member', label: 'Team Member' }
      ];
    } else if (user?.role === 'Change Manager') {
      return [
        { value: 'Team Member', label: 'Team Member' }
      ];
    } else {
      return []; // Team Members can't create employees
    }
  };

  // Check if current user can delete a specific employee
  const canDeleteEmployee = (employee) => {
    if (user?.role === 'Admin') {
      // Admin can delete anyone except the last admin
      if (employee.role === 'Admin') {
        const adminCount = employees.filter(emp => emp.role === 'Admin').length;
        return adminCount > 1; // Can't delete the last admin
      }
      return true;
    } else if (user?.role === 'Change Manager') {
      // Change Manager can only delete Team Members they created
      return employee.created_by_employee_id === user.id && employee.role === 'Team Member';
    }
    return false; // Team Members can't delete anyone
  };

  // Check if current user can edit a specific employee
  const canEditEmployee = (employee) => {
    if (user?.role === 'Admin') {
      return true; // Admin can edit anyone
    } else if (user?.role === 'Change Manager') {
      // Change Manager can edit themselves or Team Members they created
      return employee.id === user.id || 
             (employee.created_by_employee_id === user.id && employee.role === 'Team Member');
    }
    return employee.id === user.id; // Users can edit themselves
  };

  // Fetch Employees Function
  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Build query parameters for filtering
      const params = new URLSearchParams();
      if (filterManagerId && user?.role === 'Admin') {
        params.append('manager_id', filterManagerId);
      }
      
      const url = `/employees${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await apiClient.get(url);
      setEmployees(response.data.employees);
    } catch (e) {
      console.error("Failed to load employees:", e);
      const errorMsg = e.response?.data?.error || e.message || "An unknown error occurred";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [filterManagerId, user?.role]);

  // Fetch Change Managers (for Admin dropdown)
  const fetchChangeManagers = useCallback(async () => {
    if (user?.role !== 'Admin') return; // Only Admins need this
    
    try {
      const response = await apiClient.get('/change-managers');
      setChangeManagers(response.data);
    } catch (e) {
      console.error("Failed to load Change Managers:", e);
      // Don't set error state for this, as it's not critical
    }
  }, [user?.role]);

  useEffect(() => {
    fetchEmployees();
    fetchChangeManagers();
  }, [fetchEmployees, fetchChangeManagers]);

  // Clear selections when employees list changes
  useEffect(() => {
    setSelectedEmployees(new Set());
  }, [employees]);

  // Checkbox handling for bulk operations
  const handleSelectEmployee = (employeeId, isChecked) => {
    setSelectedEmployees(prev => {
      const newSet = new Set(prev);
      if (isChecked) {
        newSet.add(employeeId);
      } else {
        newSet.delete(employeeId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      // Only select employees that can be deleted
      const deletableEmployeeIds = employees
        .filter(emp => canDeleteEmployee(emp))
        .map(emp => emp.id);
      setSelectedEmployees(new Set(deletableEmployeeIds));
    } else {
      setSelectedEmployees(new Set());
    }
  };

  const getSelectableEmployeesCount = () => {
    return employees.filter(emp => canDeleteEmployee(emp)).length;
  };

  // Modal Handling (Add/Edit)
  const openAddModal = () => {
    setCurrentEmployee(null);
    setFormData({ name: '', email: '', department: '', job_position: '', role: '', assigned_manager_id: '' });
    setApiError(null);
    setIsModalOpen(true);
  };

  const openEditModal = (employee) => {
    console.log('Opening edit modal for employee:', employee);
    console.log('Current manager ID (created_by_employee_id):', employee.created_by_employee_id);
    console.log('Available Change Managers:', changeManagers);
    
    setCurrentEmployee(employee);
    setFormData({
      name: employee.name,
      email: employee.email,
      department: employee.department || '',
      job_position: employee.job_position || '',
      role: employee.role || '',
      assigned_manager_id: employee.created_by_employee_id || '' // Show current manager
    });
    setApiError(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentEmployee(null);
    setApiError(null);
    setSuccessMessage('');
  };

  // Form Handling (Add/Edit)
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // API Operations (Add/Edit)
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);
    setSuccessMessage('');
    
    // Basic client-side validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.job_position.trim() || !formData.role) {
      setApiError('Please fill in all required fields (Name, Email, Job Position, Role)');
      return;
    }
    
    // Additional validation for Admin creating Team Members
    if (user?.role === 'Admin' && formData.role === 'Team Member' && !formData.assigned_manager_id) {
      setApiError('Please assign a Change Manager for Team Member employees');
      return;
    }
    
    // Adjust URL for apiClient (remove /api prefix)
    const url = currentEmployee ? `/employees/${currentEmployee.id}` : '/employees';
    const method = currentEmployee ? 'put' : 'post'; // Axios methods are lowercase

    try {
      // Use apiClient with dynamic method
      const response = await apiClient[method](url, formData);
      
      // Show success message if available
      if (response.data.message) {
        setSuccessMessage(response.data.message);
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
      }
      
      closeModal();
      // Always refresh the employee list after successful creation/update
      await fetchEmployees();
      
      // Log success for debugging
      console.log(`Employee ${currentEmployee ? 'updated' : 'created'} successfully:`, response.data);
      
    } catch (e) {
      console.error(`Failed to ${currentEmployee ? 'update' : 'create'} employee:`, e);
      
      // More detailed error handling
      let errorMsg = "An unknown error occurred";
      if (e.response?.data?.error) {
        errorMsg = e.response.data.error;
      } else if (e.response?.status === 403) {
        errorMsg = "You don't have permission to perform this action";
      } else if (e.response?.status === 409) {
        errorMsg = "This email address is already in use";
      } else if (e.response?.status === 400) {
        errorMsg = "Invalid data provided. Please check all fields";
      } else if (e.message) {
        errorMsg = e.message;
      }
      
      setApiError(errorMsg);
      
      // If it's a plan limit error, provide additional context
      if (errorMsg.includes('limit') || errorMsg.includes('plan')) {
        setApiError(errorMsg + ' Consider upgrading your plan to add more employees.');
      }
    }
  };

  // API Operation (Delete Single)
  const handleDeleteEmployee = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    if (!employee) {
      setError('Employee not found');
      return;
    }
    
    // Check permissions before attempting delete
    if (!canDeleteEmployee(employee)) {
      setError('You do not have permission to delete this employee. You can only delete Team Members that you created.');
      return;
    }
    
    openConfirmationModal({
      title: 'Delete Employee',
      message: `Are you sure you want to delete employee "${employee.name}"? This action cannot be undone.`,
      confirmText: 'Delete Employee',
      type: 'danger',
      onConfirm: async () => {
        setConfirmationLoading(true);
        setError(null);
        try {
          // Use apiClient and adjust URL
          const response = await apiClient.delete(`/employees/${employeeId}`);
          
          // Show success message if available
          if (response.data.message) {
            setSuccessMessage(`Employee "${employee.name}" deleted successfully`);
            setTimeout(() => setSuccessMessage(''), 5000);
          }
          
          await fetchEmployees();
          console.log(`Employee ${employeeId} deleted successfully`);
          closeConfirmationModal();
        } catch (e) {
          console.error(`Failed to delete employee ${employeeId}:`, e);
          
          let errorMsg = "An unknown error occurred";
          if (e.response?.data?.error) {
            errorMsg = e.response.data.error;
          } else if (e.response?.status === 403) {
            errorMsg = "You don't have permission to delete this employee";
          } else if (e.response?.status === 404) {
            errorMsg = "Employee not found";
          } else if (e.message) {
            errorMsg = e.message;
          }
          
          setError(`Failed to delete employee: ${errorMsg}`);
          closeConfirmationModal();
        }
      }
    });
  };

  // API Operation (Bulk Delete)
  const handleBulkDelete = () => {
    if (selectedEmployees.size === 0) {
      setError('No employees selected for deletion');
      return;
    }

    const selectedEmployeesList = employees.filter(emp => selectedEmployees.has(emp.id));
    const employeeNames = selectedEmployeesList.map(emp => emp.name).join(', ');
    
    openConfirmationModal({
      title: 'Bulk Delete Employees',
      message: `Are you sure you want to delete ${selectedEmployees.size} employee(s): ${employeeNames}? This action cannot be undone.`,
      confirmText: `Delete ${selectedEmployees.size} Employee(s)`,
      type: 'danger',
      onConfirm: async () => {
        setConfirmationLoading(true);
        setError(null);
        setBulkDeleting(true); // Start loading state
        
        try {
          // Convert Set to Array for API call
          const employeeIds = Array.from(selectedEmployees);
          
          // Use bulk delete endpoint
          const response = await apiClient.delete('/employees/bulk', {
            data: { employee_ids: employeeIds }
          });
          
          // Show success message
          if (response.data.message) {
            setSuccessMessage(response.data.message);
          } else {
            setSuccessMessage(`Successfully deleted ${selectedEmployees.size} employee(s)`);
          }
          setTimeout(() => setSuccessMessage(''), 5000);
          
          // Clear selections and refresh list
          setSelectedEmployees(new Set());
          await fetchEmployees();
          closeConfirmationModal();
          
        } catch (e) {
          console.error('Failed to delete employees:', e);
          
          let errorMsg = "An unknown error occurred";
          if (e.response?.data?.error) {
            errorMsg = e.response.data.error;
          } else if (e.response?.status === 403) {
            errorMsg = "You don't have permission to delete one or more selected employees";
          } else if (e.message) {
            errorMsg = e.message;
          }
          
          setError(`Failed to delete employees: ${errorMsg}`);
          closeConfirmationModal();
        } finally {
          setBulkDeleting(false); // End loading state
        }
      }
    });
  };

  // Bulk Upload Handling
  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploadStatus('Uploading...');
    setError(null);

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      // Use apiClient and adjust URL
      const response = await apiClient.post('/employees/upload', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      const result = response.data; // Axios provides data directly
      setUploadStatus(`Upload successful! Added: ${result.imported_count}, Skipped/Errors: ${result.skipped_count}. Check console for details.`);
      if (result.errors && result.errors.length > 0) {
        console.warn("Bulk Upload Errors:", result.errors);
      }
      fetchEmployees();
    } catch (e) {
      console.error("Bulk upload failed:", e);
      const errorMsg = e.response?.data?.error || e.message || "An unknown error occurred";
      setUploadStatus(`Upload failed: ${errorMsg}`);
    } finally {
      event.target.value = null;
    }
  };

  return (
    <div className="employee-management-page">
      {/* <h2>Employee Management</h2> Removed redundant title */}

      <div className="page-actions">
        <button className="btn btn-primary" onClick={openAddModal}>+ Add Employee</button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        />
        <button className="btn btn-secondary" style={{ marginLeft: '10px' }} onClick={handleUploadClick}>Upload List</button>
        
        {/* Bulk Delete Button */}
        {selectedEmployees.size > 0 && (
          <button 
            className="btn btn-danger" 
            style={{ marginLeft: '20px' }}
            onClick={handleBulkDelete}
            disabled={bulkDeleting}
          >
            {bulkDeleting ? (
              <>⏳ Deleting {selectedEmployees.size} employee(s)...</>
            ) : (
              <>🗑️ Delete Selected ({selectedEmployees.size})</>
            )}
          </button>
        )}
        
        {/* Filter by Change Manager (Admin only) */}
        {user?.role === 'Admin' && (
          <div style={{ marginLeft: '20px', display: 'inline-block' }}>
            <label htmlFor="filterManager" style={{ marginRight: '8px', fontWeight: 'bold' }}>
              Filter by Manager:
            </label>
            <select
              id="filterManager"
              value={filterManagerId}
              onChange={(e) => setFilterManagerId(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                fontSize: '14px',
                minWidth: '200px'
              }}
            >
              <option value="">All Employees</option>
              <option value="self">Self-managed (by Admin)</option>
              {changeManagers.map(manager => (
                <option key={manager.id} value={manager.id}>
                  {manager.name} - {manager.email}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {uploadStatus && <p style={{ color: uploadStatus.startsWith('Upload failed') ? 'red' : 'green', margin: '10px 0' }}>{uploadStatus}</p>}
      {successMessage && <p style={{ color: 'green', margin: '10px 0', padding: '10px', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '4px' }}>{successMessage}</p>}
      {bulkDeleting && <p style={{ color: '#dc3545', margin: '10px 0', padding: '10px', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px', fontWeight: 'bold' }}>⏳ Deleting {selectedEmployees.size} employee(s)... This may take a moment.</p>}
      {loading && <p>Loading employees...</p>}
      {error && !loading && <p className="error-message" style={{ border: '1px solid red', padding: '10px', backgroundColor: '#ffecec' }}>Error loading employee data: {error}</p>}

      {!loading && (
        <div className="table-wrapper">
          {/* Legend */}
          <div style={{ 
            marginBottom: '15px', 
            padding: '10px', 
            backgroundColor: '#f5f5f5', 
            borderRadius: '4px', 
            fontSize: '0.9em',
            border: '1px solid #ddd'
          }}>
            {user?.role === 'Admin' ? (
              <div>
                <div style={{ marginBottom: '5px' }}>
                  <strong>Legend:</strong>
                  <span style={{ color: '#1976d2', marginLeft: '15px' }}>🔵 (You)</span>
                  <span style={{ color: '#388e3c', marginLeft: '15px' }}>🟢 (Managed) - You can edit/delete</span>
                  <span style={{ color: '#f57c00', marginLeft: '15px' }}>🟠 (Admin-created) - View only</span>
                  <span style={{ marginLeft: '15px', opacity: 0.7 }}>Grayed out buttons = No permission</span>
                </div>
                <div style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
                  <strong>Admin View:</strong> Use the "Filter by Manager" dropdown above to view employees by their assigned Change Manager
                </div>
              </div>
            ) : user?.role === 'Change Manager' ? (
              <div>
                <strong>Your Team:</strong>
                <span style={{ color: '#1976d2', marginLeft: '15px' }}>🔵 (You)</span>
                <span style={{ color: '#388e3c', marginLeft: '15px' }}>🟢 (Your Team Members)</span>
              </div>
            ) : (
              <div>
                <strong>Your Profile</strong>
              </div>
            )}
          </div>
          
          <table className="table-header">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  {getSelectableEmployeesCount() > 0 && (
                    <input
                      type="checkbox"
                      checked={selectedEmployees.size > 0 && selectedEmployees.size === getSelectableEmployeesCount()}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      title="Select/Deselect all deletable employees"
                      disabled={bulkDeleting}
                    />
                  )}
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Department</th>
                <th>Job Position</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
          </table>
          <div className="table-body-scroll">
            <table className="table-body">
              <tbody>
                {employees.length > 0 ? (
                  employees.map((employee) => {
                    const canEdit = canEditEmployee(employee);
                    const canDelete = canDeleteEmployee(employee);
                    const isManaged = employee.created_by_employee_id === user.id;
                    const isSelf = employee.id === user.id;
                    
                    // Simplified styling for Change Managers (they only see manageable employees)
                    let rowStyle = {};
                    let labelText = '';
                    let labelColor = '';
                    
                    if (user?.role === 'Admin') {
                      rowStyle.backgroundColor = isSelf ? '#e3f2fd' : (isManaged ? '#f8f9fa' : '#fff8dc');
                      if (isSelf) {
                        labelText = '(You)';
                        labelColor = '#1976d2';
                      } else if (isManaged) {
                        labelText = '(Managed)';
                        labelColor = '#388e3c';
                      } else {
                        labelText = '(Admin-created)';
                        labelColor = '#f57c00';
                      }
                    } else if (user?.role === 'Change Manager') {
                      rowStyle.backgroundColor = isSelf ? '#e3f2fd' : '#f8f9fa';
                      if (isSelf) {
                        labelText = '(You)';
                        labelColor = '#1976d2';
                      } else {
                        labelText = '(Team Member)';
                        labelColor = '#388e3c';
                      }
                    } else {
                      rowStyle.backgroundColor = '#e3f2fd';
                      labelText = '(You)';
                      labelColor = '#1976d2';
                    }
                    
                    return (
                      <tr key={employee.id} style={rowStyle}>
                        <td style={{ width: '40px', textAlign: 'center' }}>
                          {canDelete ? (
                            <input
                              type="checkbox"
                              checked={selectedEmployees.has(employee.id)}
                              onChange={(e) => handleSelectEmployee(employee.id, e.target.checked)}
                              title="Select for deletion"
                              disabled={bulkDeleting}
                            />
                          ) : (
                            <span style={{ opacity: 0.3, fontSize: '0.8em' }}>—</span>
                          )}
                        </td>
                        <td>
                          {employee.name}
                          {labelText && <span style={{ color: labelColor, fontSize: '0.8em', marginLeft: '5px' }}>{labelText}</span>}
                        </td>
                        <td>{employee.email}</td>
                        <td>{employee.department || 'N/A'}</td>
                        <td>{employee.job_position || 'N/A'}</td>
                        <td>{employee.role || 'N/A'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {canEdit ? (
                              <button className="btn-icon" title="Edit Employee" onClick={() => openEditModal(employee)}>✏️</button>
                            ) : (
                              <button className="btn-icon" title="Cannot edit this employee" disabled style={{ opacity: 0.3, cursor: 'not-allowed' }}>✏️</button>
                            )}
                            {canDelete ? (
                              <button className="btn-icon" title="Delete Employee" onClick={() => handleDeleteEmployee(employee.id)}>🗑️</button>
                            ) : (
                              <button className="btn-icon" title="Cannot delete this employee" disabled style={{ opacity: 0.3, cursor: 'not-allowed' }}>🗑️</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                      {error ? 'Could not load data.' : 'No employees found. Use \'+ Add Employee\' or \'Upload List\' to start.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal show={isModalOpen} onClose={closeModal} title={currentEmployee ? 'Edit Employee' : 'Add New Employee'}>
        <form onSubmit={handleFormSubmit}>
          {apiError && <p className="error-message" style={{ marginBottom: '15px' }}>Error: {apiError}</p>}
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required />
          </div>
          <div className="form-group">
            <label htmlFor="department">Department</label>
            <input type="text" id="department" name="department" value={formData.department} onChange={handleInputChange} />
          </div>
          <div className="form-group">
            <label htmlFor="job_position">Job Position *</label>
            <input type="text" id="job_position" name="job_position" value={formData.job_position} onChange={handleInputChange} required />
          </div>
          <div className="form-group">
            <label htmlFor="role">Role *</label>
            <select id="role" name="role" value={formData.role} onChange={handleInputChange} required>
              <option value="">Select Role</option>
              {getAvailableRoles().map(role => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
          </div>
          
          {/* Change Manager Assignment (Admin only, for Team Members) */}
          {user?.role === 'Admin' && formData.role === 'Team Member' && (
            <div className="form-group">
              <label htmlFor="assigned_manager_id">
                {currentEmployee ? 'Reassign to Change Manager' : 'Assign to Change Manager'} *
              </label>
              <select 
                id="assigned_manager_id" 
                name="assigned_manager_id" 
                value={formData.assigned_manager_id} 
                onChange={handleInputChange} 
                required
              >
                <option value="">Select Change Manager</option>
                {changeManagers.map(manager => (
                  <option key={manager.id} value={manager.id}>
                    {manager.name} ({manager.email})
                    {manager.department && ` - ${manager.department}`}
                  </option>
                ))}
              </select>
              <small style={{ color: '#666', fontSize: '0.9em', marginTop: '5px', display: 'block' }}>
                {currentEmployee 
                  ? 'Change who manages this employee' 
                  : 'This Change Manager will be able to manage this employee'
                }
              </small>
            </div>
          )}
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
            <button type="submit" className="btn btn-primary">{currentEmployee ? 'Update Employee' : 'Create Employee'}</button>
          </div>
        </form>
      </Modal>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.confirmText}
        cancelText={confirmationModal.cancelText}
        type={confirmationModal.type}
        onConfirm={confirmationModal.onConfirm}
        onClose={closeConfirmationModal}
        isLoading={confirmationModal.isLoading}
      />
    </div>
  );
}

export default EmployeeManagement;

